// <s-delivery-check> validates an Indian 6-digit pincode against the merchant's serviceable
// list (exact codes or prefixes) and shows the delivery estimate. It sits inside the product
// form, so the input is unnamed and Enter is intercepted to avoid submitting add-to-cart.
const STORAGE_KEY = 'tassya:delivery-pincode';

class SDeliveryCheck extends HTMLElement {
  #ac;
  #input;
  #result;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.#input = this.querySelector('[data-delivery-input]');
    this.#result = this.querySelector('[data-delivery-result]');
    if (!this.#input || !this.#result) return;

    this.addEventListener('command', (event) => {
      if (event.command === '--check') this.#check();
    }, opts);

    this.#input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.#check();
      }
    }, opts);

    this.#input.addEventListener('input', () => {
      this.#input.value = this.#input.value.replace(/\D/g, '').slice(0, 6);
    }, opts);

    // The header "Deliver to" picker shares the saved pincode; re-check when it changes.
    document.addEventListener("tassya:pincode", (event) => {
      if (event.detail?.source === this || !event.detail?.pin) return;
      this.#input.value = event.detail.pin;
      this.#check();
    }, opts);

    // The product-form partial re-renders on variant change; restore the buyer's last check.
    let saved = null;
    try { saved = localStorage.getItem(STORAGE_KEY); } catch {}
    if (saved) {
      this.#input.value = saved;
      this.#check();
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #check() {
    const pin = this.#input.value.trim();
    if (!/^[1-9]\d{5}$/.test(pin)) {
      this.#show(this.dataset.invalid, 'error');
      this.#input.setAttribute('aria-invalid', 'true');
      return;
    }
    this.#input.removeAttribute('aria-invalid');
    try { localStorage.setItem(STORAGE_KEY, pin); } catch {}
    document.dispatchEvent(new CustomEvent("tassya:pincode", { detail: { pin, source: this } }));

    const list = (this.dataset.pincodes || '')
      .split(/[\s,]+/)
      .map((code) => code.trim())
      .filter(Boolean);
    const serviceable = list.length === 0 || list.some((code) => pin.startsWith(code));

    this.#show(serviceable ? this.dataset.available : this.dataset.unavailable, serviceable ? 'success' : 'error');
  }

  #show(message, state) {
    this.#result.textContent = message || '';
    this.#result.dataset.state = state;
    this.#result.hidden = !message;
  }
}

if (!customElements.get('s-delivery-check')) customElements.define('s-delivery-check', SDeliveryCheck);
